<template>
  <h1 class="sr-only">mimio — 예술과 비즈니스를 연결하는 ART IP 플랫폼</h1>
  <!-- s::MAIN -->
  <div v-if="loading" class="loading" style="height: 100vh"></div>

  <div v-else-if="error" class="error">{{ error.message }}</div>

  <main v-else style="min-height: 100vh">

    <div v-if="homeMainBanner" class="main-text" @click="goToBannerUrl(homeMainBanner.linkUrl)"
         :style="{ cursor: homeMainBanner.linkUrl ? 'pointer' : 'default' }" style="background-color: #151419;">
      <template v-if="homeMainBanner.mediaType === 'MP4'">
        <video autoplay muted loop playsinline @loadeddata="onImageLoaded" style="height: 100%;object-fit: contain; ">
          <source :src="homeMainBanner.imageUrl" type="video/mp4">
        </video>
      </template>
      <template v-else>
        <img :src="homeMainBanner.imageUrl" @load="onImageLoaded" alt="Banner Image" style="width: 100%; height: 100%; object-fit: contain;">
      </template>
    </div>

    <section v-if="artists.length" class="section01">
      <div class="art-box">
        <div class="title-box">
          <h2>ART & ARTISTS</h2>
          <div class="button-box">
            <router-link :to="{ name: 'ArtistList' }">See More</router-link>
          </div>
        </div>

        <div class="slide-container">
          <div class="slide-title">
            <h3>Artists of the Month</h3>
          </div>
          <div class="slide-box" :class="isMobile ? 'mo' : 'pc'">  <!-- class: pc, mo-->
            <div class="tab-cont" v-for="(artist, index) in artists" :key="artist.uuid">
              <div class="inner">
                <div v-if="!isMobile" class="artists" @click="goToArtistDetail(artist.userAccountId)">
                  <div class="artists-txt">
                    <h3>
                      {{ artist.nameEn }}
                      <HeartButton v-model:isLike="artist.isLike" :uuid="artist.uuid" :likeType="artist.likeType"/>
                    </h3>
                    <p>{{ artist.nameKo }}</p>
                  </div>
                  <div class="img-box">
                    <figure>
                      <img :src="artist.imageUrl" :alt="artist.nameKo || artist.nameEn" loading="lazy">
                    </figure>
                  </div>
                </div>

                <div class="slide-wrap">
                  <!-- Swiper -->
                  <div class="swiper " :class="'mySwiper-' + index">
                    <!--
                      화살표는 줄마다 따로 둔다 (mimio#75).
                      메인은 아티스트 한 명당 swiper 가 하나라 전역 셀렉터로 묶으면
                      모든 줄이 같은 버튼에 걸린다 — 아래 navigation 설정과 짝이다.
                    -->
                    <button type="button" class="slide-nav slide-nav-prev swiper-button-prev"
                            :aria-label="`${artist.nameKo || artist.nameEn} 작품 이전`"></button>
                    <button type="button" class="slide-nav slide-nav-next swiper-button-next"
                            :aria-label="`${artist.nameKo || artist.nameEn} 작품 다음`"></button>
                    <div class="swiper-wrapper">
                      <div v-if="isMobile" class="swiper-slide">
                        <div class="artists" @click="goToArtistDetail(artist.userAccountId)">
                          <div class="artists-txt">
                            <h3>
                              {{ artist.nameEn }}
                            </h3>
                            <p>{{ artist.nameKo }}</p>
                          </div>
                          <div class="img-box">
                            <figure>
                              <img :src="artist.imageUrl" :alt="artist.nameKo || artist.nameEn" loading="lazy">
                            </figure>
                          </div>
                        <HeartButton v-model:isLike="artist.isLike" :uuid="artist.uuid" :likeType="artist.likeType" style="position: absolute; top: 30px; right: 15px;"/>
                        </div>
                      </div>
                      <div class="swiper-slide" v-for="(artWork, index) in artist.artWorks" :key="index+artWork.uuid">
                        <router-link
                            :to="{ name: 'ArtistDetail', params: { userAccountId: artist.userAccountId }, query: { artwork: artWork.uuid } }">
                          <img :src="artWork.imageUrl" :alt="artWork.title || artWork.titleEn" loading="lazy">
                          <div class="txt">
                            <p>{{ artWork.title || artWork.titleEn }}</p>
                            <p>{{ artWork.material }}</p>
                            <span>{{ artWork.year }}</span>
                          </div>
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <HomeSubBannerSwiper v-if="homeSubBanners" :banners="homeSubBanners"/>

    <section v-if="monthlyArticleSpecials.length" class="section03">
      <div class="article-title">
        <h3>ARTICLE</h3>
        <ul>
          <li>
            <router-link :to="{ name: 'Special' }">SPECIAL</router-link>
          </li>
          <li>
            <router-link :to="{ name: 'Exhibition' }">EXHIBITION</router-link>
          </li>
          <li>
            <router-link :to="{ name: 'Event' }">EVENT</router-link>
          </li>
          <li>
            <router-link :to="{ name: 'News' }">NEWS</router-link>
          </li>
        </ul>
      </div>

      <div class="monthly-articles-list">
        <router-link v-for="articleSpecial in validMonthlyArticleSpecials" :key="articleSpecial.uuid" :to="{ name: getRouteNameByValue(articleSpecial.likeType), params: { uuid: articleSpecial.uuid } }">
          <div class="article-wrap">
            <div class="figure-wrap">
              <figure><img :src="articleSpecial.previewContentUrl" :alt="articleSpecial.subTitle" loading="lazy"></figure>
              <div class="type-badge-wrap">
                <div :class="['type-badge', `badge-${articleSpecial.articleSpecialType?.toLowerCase()}`]">
                  {{ formatArticleType(articleSpecial.articleSpecialType) }}
                </div>
              </div>
            </div>
            <div class="article-txt">
              <div class="sub-title">
                <span>{{ articleSpecial.likeType }}</span>
                <HeartButton v-model:isLike="articleSpecial.isLike" :uuid="articleSpecial.uuid" :likeType="articleSpecial.likeType"/>
              </div>
              <div class="explaint-txt">
                <div class="artists-txt">
                  <h4><template v-for="(seg, i) in splitSubTitle(articleSpecial.subTitle)" :key="i"><span v-if="seg.paren" class="name-en">{{ seg.text }}</span><template v-else>{{ seg.text }}</template></template></h4>
                  <span class="sub">{{ articleSpecial.title }}</span>
                </div>
                <p v-safe-html="articleSpecial.homeContent"></p>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </section>

    <section v-if="articles.length" class="section04">
      <div class="swiper mySwiper4">
        <button type="button" class="slide-nav slide-nav-prev swiper-button-prev" aria-label="이전 콘텐츠"></button>
        <button type="button" class="slide-nav slide-nav-next swiper-button-next" aria-label="다음 콘텐츠"></button>
        <div class="swiper-wrapper">
          <div class="swiper-slide" v-for="article in articles" :key="article.uuid">
            <router-link :to="{ name: getRouteNameByValue(article.likeType), params: { uuid: article.uuid } }">
              <figure><img :src="article.previewImageUrl || article.imageUrl" :alt="article.title" loading="lazy"></figure>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </main>
  <!-- e::MAIN -->
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref} from 'vue';
import Swiper from 'swiper/bundle';
import 'swiper/swiper-bundle.css';
import {apiClient} from '@/services/auth-header';
import router from "@/router";
import {useAuthStore} from '@/store/auth.module';
import {useUnreadStore} from '@/store/unread.module';
import HeartButton from "@/components/ui/HeartButton.vue";
import {ARTICLE_LIKE_TYPE} from '@/common/common-enum';
import {useDeviceStore} from "@/store/deviceStore";
import {useUiStore} from "@/store/useUiStore";
import HomeSubBannerSwiper from "@/components/home/HomeSubBannerSwiper.vue";
import {goToBannerUrl} from "@/utils/bannerLink";

const uiStore = useUiStore();

const deviceStore = useDeviceStore();
const isMobile = computed(() => deviceStore.isMobile);

const authStore = useAuthStore();
const unreadStore = useUnreadStore();

// 상태 변수 선언
const loading = ref(true);
const error = ref(null);
const artists = ref([]);
const homeMainBanner = ref(null);
const homeSubBanners = ref(null);
const articles = ref([]);
const monthlyArticleSpecials = ref([]);
let tempItems = null;

const onImageLoaded = () => {
  nextTick(() => {
    handleDataLoad();
  });
};

const handleDataLoad = async () => {
  if (tempItems) {
    // 메인 베너 후 나머지 데이터를 셋팅 (순차x 필요시 수정)
    artists.value = await getArtists();
    homeSubBanners.value = await getHomeSubBanners();
    monthlyArticleSpecials.value = await getMonthlyArticleSpecials();
    articles.value = await getArticles();
    await initSwiper();
    tempItems = null;
  }
};

const getLoopableData = (data, slideWidth = 267, spacing = 11) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const screenWidth = window.innerWidth;
  const singleSlideTotal = slideWidth + spacing;
  const totalSlidesWidth = data.length * singleSlideTotal - spacing;
  const repeatCount = Math.ceil(screenWidth / totalSlidesWidth);

  const repeated = [];
  for (let i = 0; i < repeatCount; i++) {
    repeated.push(...data);
  }

  return repeated;
};

const makeLoopableArtists = (artists) => {
  return artists.map((artist) => ({
    ...artist,
    artWorks: getLoopableData(artist.artWorks),
  }));
};

const getArtists = async () => {
  return makeLoopableArtists(tempItems.artists);
};

const getHomeSubBanners = async () => {
  return tempItems.homeSubBanners;
};

const getMonthlyArticleSpecials = async () => {
  try {
    const response = await apiClient.get('/v1/articles/specials/monthly');
    return response.data.data || [];
  } catch (err) {
    console.error('Failed to fetch monthly article specials:', err);
    return [];
  }
};

const getArticles = async () => {
  return getLoopableData(tempItems.articles,287,18);
};

const getRouteNameByValue = (value) => {
  const item = ARTICLE_LIKE_TYPE.find(item => item.value === value);
  return item ? item.routeName : null;
};

const formatArticleType = (type) => {
  if (!type) return '';
  return type.charAt(0) + type.slice(1).toLowerCase();
};

// subTitle 을 괄호 구간 / 일반 구간으로 분리. 괄호 안 영문명만 작게 렌더하기 위함.
// "이동구(DONGGU LEE) & 진형(ZINHYUNG)"
//   -> [{text:'이동구'}, {text:'(DONGGU LEE)', paren:true}, {text:' & 진형'}, {text:'(ZINHYUNG)', paren:true}]
// 괄호가 없으면 구간 1개만 나오므로 기존과 동일하게 렌더된다.
const splitSubTitle = (subTitle) => {
  if (!subTitle) return [];
  return String(subTitle)
      .split(/(\([^()]*\)|（[^（）]*）)/)
      .filter(part => part !== '')
      .map(part => ({text: part, paren: /^[(（][^()（）]*[)）]$/.test(part)}));
};

const validMonthlyArticleSpecials = computed(() => {
  return monthlyArticleSpecials.value
    .filter(article => article.uuid)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
});

const goToArtistDetail = (userAccountId) => {
  // <router-link :to="{ name: 'ArtistDetail', params: { userAccountId: artist.userAccountId } }"> 로 사용시  css 깨짐.
  router.push({name: 'ArtistDetail', params: {userAccountId}});  // 라우터로 이동
};

// Swiper 초기화 함수
const swiperInstances = [];

const initSwiper = async () => {
  await nextTick();

  // 기존에 있던 swiper 인스턴스 전부 destroy
  swiperInstances.forEach(swiper => swiper.destroy(true, true));
  swiperInstances.length = 0;

  artists.value.forEach((artist, index) => {
    const swiper = new Swiper(`.mySwiper-${index}`, {
      observer: true,
      observeParents: true,
      spaceBetween: 11,
      slidesPerView: 'auto',
      loop: true,
      freeMode: true,
      autoplay: !isMobile.value ? {
        delay: 1000 * (artists.value.length),
        disableOnInteraction: false,
      } : false,
      pagination: false,
      // 줄마다 자기 버튼에만 묶는다 — 전역 셀렉터를 쓰면 세 줄이 한 버튼에 걸린다 (mimio#75)
      navigation: {
        nextEl: `.mySwiper-${index} .swiper-button-next`,
        prevEl: `.mySwiper-${index} .swiper-button-prev`,
      },
      // 번들의 a11y 모듈이 버튼 aria-label 을 "Previous slide" 로 덮어쓴다 (mimio#75)
      a11y: {
        prevSlideMessage: `${artist.nameKo || artist.nameEn} 작품 이전`,
        nextSlideMessage: `${artist.nameKo || artist.nameEn} 작품 다음`,
      },
    });

    swiperInstances.push(swiper);

    if (!isMobile.value) {
      swiper.autoplay?.stop();
      setTimeout(() => {
        swiper.autoplay?.start();
      }, index * 1000);
    }
  });

  const swiper4 = new Swiper(".mySwiper4", {
    observer: true,
    observeParents: true,
    spaceBetween: 18,
    slidesPerView: 'auto',
    loop: true,
    freeMode: false,
    centeredSlides: true,
    autoplay: {
      delay: 2500,
      // 화살표를 눌러도 자동 재생이 멈추지 않는다 (mimio#75)
      disableOnInteraction: false,
    },
    navigation: {
      nextEl: '.mySwiper4 .swiper-button-next',
      prevEl: '.mySwiper4 .swiper-button-prev',
    },
    a11y: {
      prevSlideMessage: '이전 콘텐츠',
      nextSlideMessage: '다음 콘텐츠',
    },
  });

  swiperInstances.push(swiper4);
};

const loadItems = async () => {
  try {
    uiStore.showLoading();
    const response = await apiClient.get('/v1/home');
    homeMainBanner.value = response.data.homeMainBanner;
    tempItems = response.data;
    // localStorage.setItem("menu", JSON.stringify(response.data.menu));
  } catch (err) {
    error.value = error; // 에러를 저장합니다.
  } finally {
    loading.value = false; // 작업이 끝난 후 로딩 상태를 끝냅니다.
    uiStore.hideLoading();
  }
};

onMounted(async () => {
  await loadItems();
  await unreadStore.fetchAllUnreadCounts();
});

onBeforeUnmount(() => {
  // Swiper 정리
  swiperInstances.forEach(swiper => swiper.destroy(true, true));
  swiperInstances.length = 0;
});

</script>

<style scoped>
video {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 비디오가 비율에 맞춰 크기 맞추기 */
}

.section04 .mySwiper4 .swiper-wrapper .swiper-slide figure img {
  width: 100%;
  aspect-ratio: 287.73 / 394.6;
  /* object-fit: cover; */
  border-radius: 20px;
}

/* ───────────────────────────────────────────────────────────────
   가로 슬라이드 좌우 화살표 (mimio#75)

   전까지는 화살표가 아예 없어서 가로 휠이나 드래그를 아는 사람만
   넘길 수 있었다. 두 영역(Artists of the Month · 하단 포스터) 모두
   같은 모양을 쓴다.

   Swiper 기본 CSS 의 ::after 아이콘은 지우고 직접 그린다 —
   기본 아이콘은 파란색 44px 고정이라 어두운 배경에서 튄다.
   ─────────────────────────────────────────────────────────────── */
.slide-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(21, 20, 25, 0.55);
  backdrop-filter: blur(4px);
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.slide-nav::after {
  /* Swiper 기본 폰트 아이콘 제거 */
  content: '';
  display: block;
  width: 10px;
  height: 10px;
  margin: 0 auto;
  border-top: 2px solid #fff;
  border-right: 2px solid #fff;
}

.slide-nav-prev { left: 12px; }
.slide-nav-next { right: 12px; }

.slide-nav-prev::after { transform: translateX(2px) rotate(-135deg); }
.slide-nav-next::after { transform: translateX(-2px) rotate(45deg); }

.slide-nav:hover { background: rgba(21, 20, 25, 0.8); }
.slide-nav:focus-visible { outline: 2px solid #FFD353; outline-offset: 2px; }

/* loop 라 평소엔 안 걸리지만, 넘길 게 없으면 Swiper 가 이 클래스를 붙인다 */
.slide-nav.swiper-button-disabled { opacity: 0.35; pointer-events: none; }
.slide-nav.swiper-button-lock { display: none; }

/* 모바일 — 터치 스와이프가 되더라도 버튼은 남긴다.
   화면이 좁아 작품을 가리므로 크기를 줄이고 가장자리에 붙인다. */
@media (max-width: 768px) {
  .slide-nav {
    width: 34px;
    height: 34px;
    background: rgba(21, 20, 25, 0.6);
  }

  .slide-nav::after {
    width: 8px;
    height: 8px;
    border-top-width: 1.5px;
    border-right-width: 1.5px;
  }

  .slide-nav-prev { left: 6px; }
  .slide-nav-next { right: 6px; }
}

/* Slide Title - 공통 스타일 */
.slide-title h3 {
  color: #FFD353;
  font-family: 'Poppins';
  font-size: 18px;
  font-weight: 500;
  line-height: 138%;
}

/* Monthly Articles List - 최소 컨테이너만 */
.monthly-articles-list {
  display: flex;
  flex-direction: column;
  gap: 65px;
}

/* Article List Figure with Type Badge Overlay */
.monthly-articles-list .figure-wrap {
  position: relative;
}

.monthly-articles-list .type-badge-wrap {
  position: absolute;
  top: 10px;
  right: 9px;
  z-index: 10;
}

.monthly-articles-list .type-badge {
  width: 92px;
  height: 36px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 10px;
  box-shadow: 0px 0px 20px rgba(21, 20, 25, 0.6);
  border-radius: 5px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  white-space: nowrap;
}

/* Type별 배지 스타일 */
.monthly-articles-list .type-badge.badge-column {
  background-color: #0900FF;
  color: #FFFFFF;
  box-shadow: 0px 0px 20px rgba(21, 20, 25, 0.6);
}

.monthly-articles-list .type-badge.badge-interview {
  background-color: #FFD353;
  color: #151419;
  box-shadow: 0px 0px 20px rgba(21, 20, 25, 0.6);
}

.monthly-articles-list .type-badge.badge-video {
  background-color: #FF0042;
  color: #FFFFFF;
  box-shadow: 0px 0px 20px rgba(21, 20, 25, 0.6);
}
</style>